import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, Menu, X, Instagram, MapPin } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useCart } from "@/context/CartContext";

export default function StorefrontHeader() {
    const { settings } = useSettings();
    const { totals } = useCart();
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [scrolled, setScrolled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const submitSearch = (e) => {
        e.preventDefault();
        if (search.trim()) {
            navigate(`/shop?q=${encodeURIComponent(search.trim())}`);
            setOpen(false);
        }
    };

    return (
        <>
            {/* Top bar */}
            <div className="ticker text-[11px] tracking-widest uppercase text-white/70 hidden md:block">
                <div className="container mx-auto px-4 py-2 flex items-center justify-between font-display">
                    <div className="flex items-center gap-4">
                        <span>Pan India shipping available</span>
                    </div>
                    <div className="flex items-center gap-4">
                        <Link to="/track-order" className="hover:text-[#00BCE5]" data-testid="topbar-track-link">
                            Track Order
                        </Link>
                        {settings?.instagram_url && (
                            <a
                                href={settings.instagram_url}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-1 hover:text-[#00BCE5]"
                                data-testid="topbar-instagram-link"
                            >
                                <Instagram size={12} /> {settings?.instagram_handle || "Instagram"}
                            </a>
                        )}
                    </div>
                </div>
            </div>

            <header
                className={`sticky top-0 z-40 backdrop-blur-xl transition-colors ${
                    scrolled ? "bg-[#0A0A0B]/85 border-b border-[#27272A]" : "bg-[#0A0A0B]/60"
                }`}
            >
                <div className="container mx-auto px-4 py-3 flex items-center gap-3">
                    <button
                        className="md:hidden p-2 -ml-2 text-white"
                        aria-label="Open menu"
                        onClick={() => setOpen(true)}
                        data-testid="header-menu-button"
                    >
                        <Menu size={22} />
                    </button>

                    <Link to="/" className="flex items-center gap-2 mr-4" data-testid="header-logo">
                        {settings?.logo_url ? (
                            <img
                                src={settings.logo_url}
                                alt={settings?.store_name || "Opensea"}
                                className="h-12 w-auto md:h-14 max-h-14 object-contain"
                            />
                        ) : (
                            <span className="font-display font-black text-2xl text-white tracking-tight uppercase">
                                {settings?.store_name || "Opensea"}
                            </span>
                        )}
                    </Link>

                    {/* Desktop nav */}
                    <nav className="hidden md:flex items-center gap-6 font-display uppercase tracking-wider text-sm font-semibold text-white/80">
                        <Link to="/shop" className="hover:text-[#00BCE5]" data-testid="header-shop-link">
                            Shop
                        </Link>
                        <Link to="/shop?new=1" className="hover:text-[#00BCE5]" data-testid="header-new-link">
                            New Arrivals
                        </Link>
                        <Link to="/shop?sale=1" className="hover:text-[#00BCE5]" data-testid="header-sale-link">
                            Sale
                        </Link>
                        <Link to="/track-order" className="hover:text-[#00BCE5]" data-testid="header-track-link">
                            Track
                        </Link>
                        <Link to="/contact" className="hover:text-[#00BCE5]" data-testid="header-contact-link">
                            Contact
                        </Link>
                    </nav>

                    <form onSubmit={submitSearch} className="ml-auto hidden md:flex items-center flex-1 max-w-md">
                        <div className="relative w-full">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search rods, reels, lures..."
                                className="w-full bg-[#121214] border border-[#27272A] rounded-sm py-2 pl-9 pr-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#00BCE5]"
                                data-testid="header-search-input"
                            />
                        </div>
                    </form>

                    <Link
                        to="/cart"
                        className="ml-auto md:ml-3 relative p-2 text-white hover:text-[#00BCE5]"
                        aria-label="Cart"
                        data-testid="header-cart-link"
                    >
                        <ShoppingBag size={22} />
                        {totals.count > 0 && (
                            <span
                                className="absolute -top-0.5 -right-0.5 bg-[#00BCE5] text-black text-[10px] font-black rounded-full px-1.5 py-0.5"
                                data-testid="header-cart-count"
                            >
                                {totals.count}
                            </span>
                        )}
                    </Link>
                </div>

                {/* Mobile search row */}
                <form onSubmit={submitSearch} className="md:hidden px-4 pb-3">
                    <div className="relative w-full">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/50" />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search tackle..."
                            className="w-full bg-[#121214] border border-[#27272A] rounded-sm py-2.5 pl-9 pr-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#00BCE5]"
                            data-testid="mobile-search-input"
                        />
                    </div>
                </form>
            </header>

            {/* Mobile drawer */}
            {open && (
                <div className="fixed inset-0 z-50 md:hidden" role="dialog">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
                    <div className="absolute left-0 top-0 bottom-0 w-[82%] max-w-sm bg-[#0A0A0B] border-r border-[#27272A] p-5 flex flex-col">
                        <div className="flex items-center justify-between mb-6">
                            <img
                                src={settings?.logo_url}
                                alt="Opensea"
                                className="h-11 w-auto max-h-11 object-contain"
                            />
                            <button
                                onClick={() => setOpen(false)}
                                className="p-2 text-white"
                                aria-label="Close"
                                data-testid="drawer-close-button"
                            >
                                <X size={22} />
                            </button>
                        </div>
                        <nav className="flex flex-col gap-1 font-display uppercase tracking-wider text-base font-semibold">
                            {[
                                { to: "/", label: "Home" },
                                { to: "/shop", label: "Shop All" },
                                { to: "/shop?new=1", label: "New Arrivals" },
                                { to: "/shop?sale=1", label: "Sale" },
                                { to: "/shop?featured=1", label: "Best Sellers" },
                                { to: "/track-order", label: "Track Order" },
                                { to: "/contact", label: "Contact" },
                            ].map((l) => (
                                <Link
                                    key={l.to + l.label}
                                    to={l.to}
                                    onClick={() => setOpen(false)}
                                    className="px-3 py-3 rounded-sm hover:bg-[#121214] hover:text-[#00BCE5] text-white"
                                    data-testid={`drawer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    {l.label}
                                </Link>
                            ))}
                        </nav>
                        <div className="mt-auto pt-6 border-t border-[#27272A] text-white/70 text-sm">
                            {settings?.instagram_url && (
                                <a
                                    href={settings.instagram_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-2 mb-2 hover:text-[#00BCE5]"
                                    data-testid="drawer-instagram-link"
                                >
                                    <Instagram size={14} /> {settings?.instagram_handle || "Instagram"}
                                </a>
                            )}
                            {settings?.address && (
                                <div className="flex items-center gap-2">
                                    <MapPin size={14} /> {settings.address}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
